import React, { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { postInstoreCardLink } from 'core/actions/member';
import { selectIsMobileMQ } from 'core/modules/MediaQueries/selectors';
import ComponentErrorBoundary from 'core/modules/ErrorBoundaries/ComponentErrorBoundary';
import { useApiFeedbackManager } from 'core/hooks/useApiFeedbackManager';
import { selectDynamicCardLinksFeedback } from 'core/modules/Instore2/selectors';
import { clearInstoreLinkFailure } from 'core/modules/Instore2/actions';
import { selectEasyLinkSuppression } from 'core/selectors/user';
import { ME_PAGE_INSTORE } from 'core/modules/Instore2/utils/constants';
import {
  Cards, LinkWarning, Disclaimer, UnlinkConfirmation, ManageCardsButton, InstoreRebate, DaysToExpire,
  useUnlinkOfferButton,
} from 'core/modules/Instore2/partials';
import { InstoreMotto, LinkedCards, useToggleDetailsButton } from '../partials';

import config from '../configs';

function WithLinkedCardsView({ offer, isOpenByDefault }) {
  const dispatch = useDispatch();
  const {
    disclaimer, offerId, hasEligibleCards, eligibleCards, rclonMerchantName,
  } = offer;
  const easyLinkSuppression = useSelector(selectEasyLinkSuppression);
  const dynamicCardLinksFeedback = useSelector(selectDynamicCardLinksFeedback);
  const isMobileMQ = useSelector(selectIsMobileMQ);
  const { getUnlinkOfferButtonText, disclaimerTitle } = config;

  const { ToggleDetailsButton, toggleDetails, isDetailsExpanded } = useToggleDetailsButton(isOpenByDefault);
  const { clearFeedback, feedbackData } = useApiFeedbackManager({
    data: dynamicCardLinksFeedback,
    callbackAction: clearInstoreLinkFailure,
  });

  const {
    UnlinkOfferButton, shouldShowConfirm, showConfirmMessage, hideConfirmMessage,
  } = useUnlinkOfferButton(ME_PAGE_INSTORE, rclonMerchantName);

  const { hasLinkFailedFeedback } = feedbackData;

  useEffect(() => {
    if (!easyLinkSuppression && hasEligibleCards) {
      // autolink unlinked cards
      eligibleCards.forEach(({ cardId }) => {
        dispatch(postInstoreCardLink(offerId, { cardId }, {}, { autoLink: true, initiator: ME_PAGE_INSTORE, offer }));
      });
    }
  }, [easyLinkSuppression, hasEligibleCards]);

  return (
    <div
      id="instore"
      className="mn_meInstoreOffer mn_withLinkedCardsView"
      data-merchant-name={rclonMerchantName}
    >
      <LinkWarning hasError={hasLinkFailedFeedback} feedbackData={feedbackData} />
      <div className="mn_mainDetails">
        <div className="mn_leftSection">
          <InstoreMotto />
          <InstoreRebate offer={offer} />
          <DaysToExpire offer={offer} />
        </div>
        <div className="mn_rightSection">
          <LinkedCards offer={offer} />
          <ToggleDetailsButton toggleDetails={toggleDetails} isDetailsExpanded={isDetailsExpanded} />
        </div>
      </div>
      {isDetailsExpanded &&
        <div className="mn_moreDetails">
          <Cards offer={offer} clearWarning={clearFeedback} initiator={ME_PAGE_INSTORE} />
          <div className="mn_buttons">
            <UnlinkOfferButton onClickCallback={showConfirmMessage}>
              {getUnlinkOfferButtonText(isMobileMQ)}
            </UnlinkOfferButton>
            <ManageCardsButton />
          </div>
          <UnlinkConfirmation
            shouldShowConfirm={shouldShowConfirm}
            offer={offer}
            onHideClick={hideConfirmMessage}
            onUnlinkClick={clearFeedback}
            initiator={ME_PAGE_INSTORE}
          />
          <Disclaimer title={disclaimerTitle}>{disclaimer}</Disclaimer>
        </div>
      }
    </div>
  );
}

export default (props) => <ComponentErrorBoundary><WithLinkedCardsView {...props} /></ComponentErrorBoundary>;
